import { takeStashedInviteToken } from '@jbroll/rowboat-sharing-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useAuthor, useSession } from '@/rowboat';
import { PhotoUploadsProvider } from './PhotoUploads';
import { SyncBanner } from './SyncBanner';

/**
 * Never blocks: anonymous users get the full app in a local-only store, with the
 * SyncBanner as the persistent Signup-to-Sync affordance. Signing up adopts the
 * local store into the account (`useAnonClaim` in the provider).
 */
export function AuthGate({
  onInvite,
  onMenu,
  children,
}: {
  /** Called with an invite token stashed before an OAuth sign-in, once that sign-in lands. */
  onInvite: (token: string) => void;
  onMenu: () => void;
  children: ReactNode;
}) {
  const { isPending } = useSession();
  const author = useAuthor();
  // Latch on first settle: session refetches flip isPending back to true, and unmounting
  // the app on every blip would wipe view state, form drafts, and the photo upload queue
  // drain. After the first settle the app stays mounted on stale session state.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!isPending) setSettled(true);
  }, [isPending]);
  const onInviteRef = useRef(onInvite);
  onInviteRef.current = onInvite;
  useEffect(() => {
    if (!settled || !author) return;
    // Storage the browser refuses held nothing: a refused stash sends Google sign-in back to the
    // invite page itself.
    const taken = takeStashedInviteToken();
    if (taken.available && taken.token !== null) onInviteRef.current(taken.token);
  }, [settled, author]);
  if (!settled) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-surface-secondary">
        <p className="text-sm text-content-tertiary">Loading…</p>
      </main>
    );
  }
  return (
    <PhotoUploadsProvider>
      <SyncBanner author={author} onMenu={onMenu} />
      {children}
    </PhotoUploadsProvider>
  );
}
